import { IconExternalLink } from '@tabler/icons-react'

import { useComponentTranslation } from '@/hooks/use-component-translation'
import { getMarketingUrl } from '@/lib/urls'
import { cn } from '@/lib/utils'
import { useLanguageStore } from '@/stores/language'
import { useWhiteLabelStore } from '@/stores/white-label'

import locales from './locales'

interface LegalFooterProps {
  className?: string
  /**
   * `surface` is the standalone strip at the bottom of the page. `inline` drops
   * the strip chrome so the block can sit inside the auth form column.
   */
  variant?: 'surface' | 'inline'
}

export function LegalFooter({
  className,
  variant = 'surface',
}: LegalFooterProps) {
  const { t } = useComponentTranslation(locales)
  const { companyName, isWhiteLabel } = useWhiteLabelStore()
  const locale = useLanguageStore((state) => state.locale)

  const isInline = variant === 'inline'

  // The inline variant renders a <div>, not a <footer>: the surface strip is
  // the page's one contentinfo landmark, and the inline block is a repeat of
  // the same links inside the auth column — a second landmark would only add
  // screen-reader noise.
  const Root = isInline ? 'div' : 'footer'

  const linkClassName = cn(
    isInline
      ? 'hover:text-foreground inline-flex items-center gap-1 whitespace-nowrap underline-offset-4 hover:underline'
      : 'text-link inline-flex items-center gap-1 whitespace-nowrap underline-offset-4 hover:underline',
  )

  return (
    <Root
      className={cn(
        isInline
          ? 'w-full'
          : 'bg-background/50 w-full border-t py-6 backdrop-blur-sm',
        className,
      )}
    >
      <div
        className={cn(
          isInline
            ? 'text-muted-foreground space-y-2 text-center text-sm'
            : 'text-muted-foreground mx-auto space-y-2 px-4 text-center text-sm',
        )}
      >
        <p>
          © {isWhiteLabel ? companyName : 'Signater'} 2026.{' '}
          {t('allRightsReserved')}
        </p>
        {!isWhiteLabel && (
          <div
            className={cn(
              isInline
                ? 'flex flex-wrap justify-center gap-x-5 gap-y-1'
                : 'flex flex-wrap justify-center gap-x-5 gap-y-1',
            )}
          >
            <a
              href={getMarketingUrl('terms', locale)}
              target="_blank"
              rel="noopener noreferrer"
              className={linkClassName}
            >
              {t('termsOfUse')}
              <IconExternalLink className="size-3" />
            </a>
            <a
              href={getMarketingUrl('cookies', locale)}
              target="_blank"
              rel="noopener noreferrer"
              className={linkClassName}
            >
              {t('cookieUsage')}
              <IconExternalLink className="size-3" />
            </a>
            <a
              href={getMarketingUrl('privacy', locale)}
              target="_blank"
              rel="noopener noreferrer"
              className={linkClassName}
            >
              {t('privacyPolicy')}
              <IconExternalLink className="size-3" />
            </a>
          </div>
        )}
      </div>
    </Root>
  )
}
